import React from 'react';
import { string, arrayOf, shape } from 'prop-types';
import { Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { spacing } from 'TMDesignSystem/spacing';
import borderRadius from 'TMDesignSystem/borders';
import Arrow from 'TMComponents/Icons/Arrow';
import Pill from 'TMComponents/Pill';

const Card = styled(Link)`
    display: flex;
    flex-direction: column;
    background: ${({ variant, theme }) => {
        if (variant === 'primary') return theme.colorSystem.primary[4];
        if (variant === 'outline') return 'transparent';
        return theme.colorSystem.primary[3];
    }};
    border: ${({ variant, theme }) =>
        variant === 'outline'
            ? `1px solid ${theme.colorSystem.UIElements[4]}`
            : 'none'};
    border-radius: ${borderRadius.medium};
    box-shadow: ${({ variant }) =>
        variant === 'outline' ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
    overflow: hidden;
    text-decoration: none;
    transition: transform 0.2s, box-shadow 0.2s;
    height: 100%;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    }
`;

const ImageWrapper = styled.div`
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;

    .gatsby-image-wrapper {
        height: 100%;
    }
`;

const CardContent = styled.div`
    padding: ${spacing.large} ${spacing.mediumLarge} ${spacing.mediumLarge}
        ${spacing.mediumLarge};
    display: flex;
    flex-direction: column;
    flex: 1;
    position: relative;
`;

const BadgeWrapper = styled.div`
    margin-bottom: ${spacing.medium};
`;

const Title = styled.h3`
    font-size: ${spacing.mediumLarge};
    font-weight: 700;
    line-height: 1.3;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin: 0 0 ${spacing.medium} 0;
`;

const Excerpt = styled.p`
    font-size: ${spacing.medium};
    font-weight: 400;
    line-height: 1.6;
    color: ${({ theme }) => theme.colorSystem.UIText[1]};
    margin: 0 0 ${spacing.mediumLarge} 0;
    flex: 1;
`;

const ReadMore = styled.span`
    font-size: ${spacing.medium};
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    display: inline-flex;
    align-items: center;
    gap: ${spacing.small};
    margin-bottom: ${spacing.mediumLarge};

    svg {
        width: 17px;
        height: 20px;
    }
`;

const TagsContainer = styled.div`
    background: ${({ theme }) => theme.colorSystem.UIElements[5]};
    border: 2px solid ${({ theme }) => theme.colorSystem.UIElements[9]};
    padding: ${spacing.medium};
    border-radius: ${borderRadius.small};
    margin-top: auto;
`;

const TagsLabel = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${({ theme }) => theme.colorSystem.primary[1]};
    margin-bottom: ${spacing.small};
`;

const Tags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing.small};
`;

const Tag = styled.span`
    padding: 6px ${spacing.medium};
    background: ${({ theme }) => theme.colorSystem.primary[4]};
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.colorSystem.primary[3]};
`;

/**
 * Used for cards built from the blog search index, which carries a plain
 * image URL rather than a processed `gatsbyImageData` object.
 */
const FallbackImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

const BlogCard = ({
    title,
    slug,
    image,
    imageUrl,
    excerpt,
    tags,
    variant
}) => (
    <Card to={`/blog/${slug}/`} variant={variant}>
        {image && (
            <ImageWrapper>
                <GatsbyImage image={image} alt={title} />
            </ImageWrapper>
        )}
        {!image && imageUrl && (
            <ImageWrapper>
                <FallbackImage src={imageUrl} alt={title} loading="lazy" />
            </ImageWrapper>
        )}
        <CardContent>
            {variant === 'press' && (
                <BadgeWrapper>
                    <Pill variant="press_release" />
                </BadgeWrapper>
            )}
            <Title>{title}</Title>
            <Excerpt>{excerpt}</Excerpt>
            <ReadMore>
                Read more
                <Arrow color="currentColor" />
            </ReadMore>
            {variant !== 'press' && tags && tags.length > 0 && (
                <TagsContainer>
                    <TagsLabel>Tagged in:</TagsLabel>
                    <Tags>
                        {tags.map((tag) => (
                            <Tag key={tag}>{tag}</Tag>
                        ))}
                    </Tags>
                </TagsContainer>
            )}
        </CardContent>
    </Card>
);

BlogCard.propTypes = {
    title: string.isRequired,
    slug: string.isRequired,
    image: shape({}),
    imageUrl: string,
    // editor-entered, so not guaranteed on every article
    excerpt: string,
    tags: arrayOf(string),
    variant: string
};

BlogCard.defaultProps = {
    image: undefined,
    imageUrl: undefined,
    excerpt: '',
    tags: [],
    variant: 'default'
};

export default BlogCard;
